import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormField } from '../../ui';
import { useClients, useAccountsByClient } from '../../../services/queries';
import { ROUTE_PATTERNS, buildRoute } from '../../../constants/Paths';

/**
 * Client -> account switcher.
 *
 * `clientId` used to be local state initialised to '', with no way to learn the
 * client from the account in the URL. On a deep link that meant the client
 * select read "Choose a client" and the account select stayed `disabled` -
 * while you were looking at that very account. `account` (fetched by the page,
 * with its client populated) seeds the client, so the switcher shows where you
 * actually are and lets you move without re-picking what you already chose.
 */
export function AccountPicker({ currentAccountId, account, compact = false }) {
	const navigate = useNavigate();
	const { data: clients = [] } = useClients();

	// Seeded from the account, and re-seeded when you navigate to an account
	// under a different client. A user choosing a client by hand overrides it
	// until the route changes again.
	const accountClientId = account?.client?._id || account?.client || null;
	const [clientId, setClientId] = useState(accountClientId || '');

	useEffect(() => {
		if (accountClientId) setClientId(String(accountClientId));
	}, [accountClientId]);

	const { data: accounts = [] } = useAccountsByClient(clientId);

	const selectCls = 'w-full rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-600 disabled:bg-muted';

	const clientSelect = (
		<select
			value={clientId}
			onChange={(e) => setClientId(e.target.value)}
			className={selectCls}
			aria-label="Client"
		>
			<option value="">Choose a client</option>
			{clients.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
		</select>
	);

	const accountSelect = (
		<select
			disabled={!clientId}
			value={currentAccountId || ''}
			onChange={(e) => e.target.value && navigate(buildRoute(ROUTE_PATTERNS.HEARTBEAT_ACCOUNT, { accountId: e.target.value }))}
			className={selectCls}
			aria-label="Account"
		>
			<option value="">Choose an account</option>
			{accounts.map((a) => <option key={a._id} value={a._id}>@{a.handle} · {a.platform}</option>)}
		</select>
	);

	// Inside the identity header the labels are redundant: the header already
	// says which account and client you are on.
	if (compact) {
		return (
			<div className="grid gap-2 sm:grid-cols-2">
				{clientSelect}
				{accountSelect}
			</div>
		);
	}

	return (
		<div className="grid gap-4 sm:grid-cols-2">
			<FormField label="Client">{clientSelect}</FormField>
			<FormField label="Account">{accountSelect}</FormField>
		</div>
	);
}
